/** @typedef {import('./BalancedNode').BalancedNode} BalancedNode */

/**
 * When the backend returns `samePreviousNodes: {}` for all nodes, computes
 * depth-based groups as a fallback: nodes at the same depth in the
 * `previousNodes` DAG are freely reorderable with each other.
 * Returns the array unchanged if any node already has groups from the backend.
 *
 * Only `JOB` nodes participate; `WORKFLOW` nodes (which merely define tree
 * groups) are passed through untouched so they never pollute the reorder groups.
 *
 * @param {BalancedNode[]} nodes
 * @returns {BalancedNode[]}
 */
export const computeNodeGroups = (allNodes) => {
  if (!allNodes?.length) return allNodes ?? []
  const others = allNodes.filter(n => n.nodeType !== 'JOB')
  const nodes = allNodes.filter(n => n.nodeType === 'JOB')
  if (!nodes.length) return allNodes
  const hasGroups = nodes.some(n => {
    const sp = n.samePreviousNodes
    if (!sp) return false
    return sp instanceof Map ? sp.size > 0 : Object.keys(sp).length > 0
  })
  if (hasGroups) return allNodes

  const nodeById = Object.fromEntries(nodes.map(n => [n.id, n]))
  const depthCache = {}
  const getDepth = (id) => {
    if (id in depthCache) return depthCache[id]
    depthCache[id] = -1 // cycle guard
    const node = nodeById[id]
    if (!node?.previousNodes?.length) return (depthCache[id] = 0)
    const d = 1 + Math.max(...node.previousNodes.map(ref => getDepth(ref.id)))
    return (depthCache[id] = d)
  }
  nodes.forEach(n => getDepth(n.id))

  const byDepth = {}
  for (const [id, d] of Object.entries(depthCache)) {
    if (d < 0) continue
    if (!byDepth[d]) byDepth[d] = []
    byDepth[d].push(id)
  }

  const augmentedJobs = nodes.map(n => {
    const d = depthCache[n.id] ?? 0
    const mates = (byDepth[d] ?? []).filter(id => id !== n.id)
    return { ...n, samePreviousNodes: mates.length ? { [String(d)]: mates } : {} }
  })
  return [...augmentedJobs, ...others]
}

/**
 * Placement / reorder constraint helpers for the balanced drag-and-drop UI.
 *
 * All ordering rules apply *within a single column* (over the node jobOrder).
 * Three node fields drive the rules:
 * - `previousNodes`: the node cannot be placed before any of these.
 * - `nextNodes`:     the node cannot be placed after any of these.
 * - `samePreviousNodes`: a `Map<groupId, nodeId[]>` (may arrive as a plain object
 *   over JSON). A node may only be reordered relative to its group-mates; the
 *   relative order of non-mates must be preserved. Groups are never intermixed.
 *
 * `insertIndex` is always expressed in `0..base.length`, where `base` is the
 * column list with the dragged node removed (so the dragged node's own current
 * position never skews the math).
 *
 * @module balancedConstraints
 */

/**
 * Returns the group member-id arrays of a node, tolerating a real `Map`, a plain
 * object (post-JSON) or a missing value.
 *
 * @param {BalancedNode} node
 * @returns {string[][]}
 */
export const sameGroupEntries = (node) => {
  const sp = node?.samePreviousNodes
  if (!sp) return []
  if (sp instanceof Map) return [...sp.values()]
  return Object.values(sp)
}

/**
 * Set of node ids the given node may be reordered with (union of all its groups,
 * excluding itself).
 *
 * @param {BalancedNode} node
 * @returns {Set<string>}
 */
export const getGroupMateIds = (node) => {
  const ids = new Set()
  for (const members of sameGroupEntries(node)) {
    for (const id of (members ?? [])) ids.add(id)
  }
  ids.delete(node?.id)
  return ids
}

/**
 * Column list without the dragged node.
 *
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {string} draggedId
 * @returns {BalancedNode[]}
 */
const baseList = (orderedColumnNodes, draggedId) =>
  (orderedColumnNodes ?? []).filter(n => n.id !== draggedId)

/**
 * Maps a "drop before this card" target to an insert index in `base`.
 * `beforeNodeId == null` means append at the end.
 *
 * @param {BalancedNode[]} base
 * @param {string|null} beforeNodeId
 * @returns {number}
 */
export const insertIndexForBefore = (base, beforeNodeId) => {
  if (beforeNodeId == null) return base.length
  const i = base.findIndex(n => n.id === beforeNodeId)
  return i < 0 ? base.length : i
}

/**
 * Required relative order of the dragged node `D` against another column node `O`.
 * Resolution order: explicit previous/next (bidirectional) → same group (free) →
 * fallback to `node.order`.
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode} other
 * @returns {'before'|'after'|'free'|'conflict'} 'before' = D must precede O
 */
const requiredOrder = (draggedNode, other) => {
  const dPrev = new Set((draggedNode.previousNodes ?? []).map(r => r.id))
  const dNext = new Set((draggedNode.nextNodes ?? []).map(r => r.id))
  const oPrev = new Set((other.previousNodes ?? []).map(r => r.id))
  const oNext = new Set((other.nextNodes ?? []).map(r => r.id))

  const mustAfter = dPrev.has(other.id) || oNext.has(draggedNode.id) // D after O
  const mustBefore = dNext.has(other.id) || oPrev.has(draggedNode.id) // D before O
  if (mustBefore && mustAfter) return 'conflict'
  if (mustBefore) return 'before'
  if (mustAfter) return 'after'

  // Group-mates can be freely reordered relative to each other.
  if (getGroupMateIds(draggedNode).has(other.id)) return 'free'

  // No direct relation: fall back to node.order.
  const dOrder = draggedNode.order
  const oOrder = other.order
  if (dOrder == null || oOrder == null || dOrder === oOrder) return 'free'
  return dOrder < oOrder ? 'before' : 'after'
}

/**
 * Whether the dragged node may be inserted at `insertIndex` in the column.
 * After inserting D at `insertIndex`, base elements at index `j < insertIndex`
 * end up before D and those at `j >= insertIndex` end up after D.
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {number} insertIndex
 * @returns {boolean}
 */
export const canInsertAt = (draggedNode, orderedColumnNodes, insertIndex) => {
  if (!draggedNode) return false
  const base = baseList(orderedColumnNodes, draggedNode.id)
  if (insertIndex < 0 || insertIndex > base.length) return false
  for (let j = 0; j < base.length; j++) {
    const order = requiredOrder(draggedNode, base[j])
    if (order === 'conflict') return false
    if (order === 'before' && j < insertIndex) return false // O must stay after D
    if (order === 'after' && j >= insertIndex) return false // O must stay before D
  }
  return true
}

/**
 * Whether the dragged node may be inserted before the given card (or appended
 * when `beforeNodeId == null`).
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {string|null} beforeNodeId
 * @returns {boolean}
 */
export const canInsertBefore = (draggedNode, orderedColumnNodes, beforeNodeId) => {
  if (!draggedNode) return false
  const base = baseList(orderedColumnNodes, draggedNode.id)
  return canInsertAt(draggedNode, orderedColumnNodes, insertIndexForBefore(base, beforeNodeId))
}

/**
 * All insert indices (0..base.length) where the dragged node fits.
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode[]} orderedColumnNodes
 * @returns {number[]}
 */
export const getValidInsertIndices = (draggedNode, orderedColumnNodes) => {
  const base = baseList(orderedColumnNodes, draggedNode?.id)
  const out = []
  for (let i = 0; i <= base.length; i++) {
    if (canInsertAt(draggedNode, orderedColumnNodes, i)) out.push(i)
  }
  return out
}

/**
 * Whether the dragged node fits anywhere in the column.
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode[]} orderedColumnNodes
 * @returns {boolean}
 */
export const canPlaceInColumn = (draggedNode, orderedColumnNodes) =>
  getValidInsertIndices(draggedNode, orderedColumnNodes).length > 0

/**
 * Base insert index that moves the dragged node to the opposite side of a target
 * node (a reorder "swap" that crosses the target). `base` is the column without
 * the dragged node.
 *
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {string} draggedId
 * @param {string} targetId
 * @returns {number} index in `0..base.length`, or -1 when either node is absent
 */
const reorderTargetIndex = (orderedColumnNodes, draggedId, targetId) => {
  const ids = (orderedColumnNodes ?? []).map(n => n.id)
  const dPos = ids.indexOf(draggedId)
  const xPos = ids.indexOf(targetId)
  if (dPos < 0 || xPos < 0) return -1
  const baseX = ids.filter(id => id !== draggedId).indexOf(targetId)
  return dPos < xPos ? baseX + 1 : baseX
}

/**
 * Id to insert the dragged node before so it crosses `targetId` (`null` = append
 * at the end), or `undefined` when the target is not present.
 *
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {string} draggedId
 * @param {string} targetId
 * @returns {string|null|undefined}
 */
export const reorderBeforeId = (orderedColumnNodes, draggedId, targetId) => {
  const idx = reorderTargetIndex(orderedColumnNodes, draggedId, targetId)
  if (idx < 0) return undefined
  const base = (orderedColumnNodes ?? []).filter(n => n.id !== draggedId)
  return idx >= base.length ? null : base[idx].id
}

/**
 * Whether the dragged node can be reordered by dropping it onto `targetId`:
 * the target must be a group-mate and crossing it must yield a valid arrangement.
 *
 * @param {BalancedNode} draggedNode
 * @param {BalancedNode[]} orderedColumnNodes
 * @param {string} targetId
 * @returns {boolean}
 */
export const canReorderOnto = (draggedNode, orderedColumnNodes, targetId) => {
  if (!draggedNode || targetId === draggedNode.id) return false
  if (!getGroupMateIds(draggedNode).has(targetId)) return false
  const idx = reorderTargetIndex(orderedColumnNodes, draggedNode.id, targetId)
  if (idx < 0) return false
  return canInsertAt(draggedNode, orderedColumnNodes, idx)
}

/* -------------------------------------------------------------------------- */
/* Line advancement (position floor / ceiling)                                */
/* -------------------------------------------------------------------------- */

/**
 * Line advancement rule: a node may only sit in a column whose *position*
 * is greater than or equal to its predecessors' positions and less
 * than or equal to its already-placed successors' positions.
 *
 * These helpers are **mode-agnostic**: callers resolve every node's position
 * (`positionByNodeId`) and the candidate column position beforehand (in TYPE mode
 * the position is the column number; in LINE mode the device `positionInLine`).
 *
 * IMPORTANT: run these on the **raw backend nodes** (`balancedData.nodes`), whose
 * `samePreviousNodes` keys are real predecessor node ids. Never on
 * `computeNodeGroups`-augmented nodes, whose synthetic depth keys are not node ids
 * and would corrupt the `groupId` semantics.
 */

/**
 * Ids of the predecessors that impose a position floor on `node`.
 *
 * When the node carries `samePreviousNodes` group keys that are real node ids
 * (backend groups), those keys (the shared previous node) are the predecessors;
 * otherwise the node's `previousNodes` ids are used. A group whose `groupId` is
 * not a real node (the group is "first" in the flow) therefore falls back to
 * `previousNodes` — empty for a root group, yielding a floor of 1.
 *
 * @param {BalancedNode} node
 * @param {Object<string, BalancedNode>} nodeById - Raw JOB nodes keyed by id
 * @returns {string[]}
 */
export const predecessorIds = (node, nodeById) => {
  const sp = node?.samePreviousNodes
  const keys = sp instanceof Map ? [...sp.keys()] : sp ? Object.keys(sp) : []
  const realGroupKeys = keys.filter(k => nodeById?.[k])
  if (realGroupKeys.length) return realGroupKeys
  return (node?.previousNodes ?? []).map(r => r.id)
}

/**
 * Reverse map of the predecessor edges: for each node id, the list of JOB nodes
 * that hold it among their {@link predecessorIds} (i.e. its successors).
 *
 * @param {BalancedNode[]} nodes - Raw backend nodes
 * @returns {Object<string, BalancedNode[]>}
 */
export const buildSuccessorsMap = (nodes) => {
  const jobs = (nodes ?? []).filter(n => n.nodeType === 'JOB')
  const nodeById = Object.fromEntries(jobs.map(n => [n.id, n]))
  const map = {}
  for (const node of jobs) {
    for (const predId of predecessorIds(node, nodeById)) {
      if (!map[predId]) map[predId] = []
      map[predId].push(node)
    }
  }
  return map
}

/**
 * Lowest column position where `node` may be placed (line-advancement floor).
 * Defaults to 1. A placed predecessor contributes its position; an unplaced one
 * cascades to its own predecessors. Memoised with a cycle guard.
 *
 * @param {BalancedNode} node
 * @param {{ nodeById: Object<string, BalancedNode>, positionByNodeId: Object<string, number> }} ctx
 * @returns {number}
 */
export const computePositionFloor = (node, ctx) => {
  const { nodeById, positionByNodeId } = ctx
  const cache = {}
  const floorOfNode = (n) => {
    if (n.id in cache) return cache[n.id]
    cache[n.id] = 1 // cycle guard
    const preds = predecessorIds(n, nodeById)
    const value = preds.length ? Math.max(...preds.map(floorOfId)) : 1
    cache[n.id] = value
    return value
  }
  const floorOfId = (id) => {
    const n = nodeById[id]
    if (!n) return 1 // not a real node => "first" predecessor
    if (positionByNodeId[id] != null) return positionByNodeId[id]
    return floorOfNode(n)
  }
  return node ? floorOfNode(node) : 1
}

/**
 * Highest column position where `node` may be placed (line-advancement ceiling),
 * imposed by its already-placed successors so they are never invalidated.
 * Defaults to `Infinity` (no ceiling). A placed successor contributes its
 * position; an unplaced one cascades to its own successors. Memoised with a
 * cycle guard. Group siblings are not successors of each other, so they never
 * cap one another.
 *
 * @param {BalancedNode} node
 * @param {{ positionByNodeId: Object<string, number>, successorsById: Object<string, BalancedNode[]> }} ctx
 * @returns {number}
 */
export const computePositionCeiling = (node, ctx) => {
  const { positionByNodeId, successorsById } = ctx
  const cache = {}
  const ceilOfNode = (n) => {
    if (n.id in cache) return cache[n.id]
    cache[n.id] = Infinity // cycle guard
    const succs = successorsById[n.id] ?? []
    const value = succs.length ? Math.min(...succs.map(ceilOfSucc)) : Infinity
    cache[n.id] = value
    return value
  }
  const ceilOfSucc = (s) => {
    if (positionByNodeId[s.id] != null) return positionByNodeId[s.id]
    return ceilOfNode(s)
  }
  return node ? ceilOfNode(node) : Infinity
}

/**
 * The inclusive `[min, max]` column-position range allowed for `node`.
 *
 * @param {BalancedNode} node
 * @param {{ nodeById: Object, positionByNodeId: Object, successorsById: Object }} ctx
 * @returns {{ min: number, max: number }}
 */
export const getPositionRange = (node, ctx) => ({
  min: computePositionFloor(node, ctx),
  max: computePositionCeiling(node, ctx)
})

/**
 * Whether `candidatePosition` falls within `node`'s allowed line-advancement range.
 *
 * @param {number} candidatePosition - Resolved position of the target column
 * @param {BalancedNode} node
 * @param {{ nodeById: Object, positionByNodeId: Object, successorsById: Object }} ctx
 * @returns {boolean}
 */
export const isPositionAllowed = (candidatePosition, node, ctx) => {
  if (candidatePosition == null) return false
  const { min, max } = getPositionRange(node, ctx)
  return candidatePosition >= min && candidatePosition <= max
}
